import { useLocalStorageState } from "@outschool/local-storage";
import { dayjs } from "@outschool/time";
import { useCallback, useMemo } from "react";

interface BannerDismissal {
  dismissedBanner: string;
  dismissedAt: string;
}

const BANNER_DISMISSAL_KEY = "banner_dismissal_state";

/**
 * Hook to manage banner dismissal state in localStorage.
 * When a banner is dismissed, it will never show again.
 * When any banner is dismissed, no other banners will show until the next day (browser timezone).
 */
export function useBannerDismissal() {
  const [dismissals, setDismissals] = useLocalStorageState<BannerDismissal[]>(
    BANNER_DISMISSAL_KEY,
    [],
  );

  const shouldShowBannersToday = useMemo(() => {
    // If any banner was dismissed today, don't show other banners today
    const wasBannerDismissedToday = dismissals.some(dismissal =>
      dayjs(dismissal.dismissedAt).isSame(dayjs(), "day"),
    );
    return !wasBannerDismissedToday;
  }, [dismissals]);

  const dismissBanner = useCallback(
    (bannerName: string) => {
      const now = new Date().toISOString();
      const existingIndex = dismissals.findIndex(
        d => d.dismissedBanner === bannerName,
      );

      if (existingIndex >= 0) {
        // Update existing dismissal timestamp
        const updated = [...dismissals];
        updated[existingIndex] = {
          dismissedBanner: bannerName,
          dismissedAt: now,
        };
        setDismissals(updated);
      } else {
        // Add new dismissal
        setDismissals([
          ...dismissals,
          { dismissedBanner: bannerName, dismissedAt: now },
        ]);
      }
    },
    [dismissals, setDismissals],
  );

  const isBannerDismissed = useCallback(
    (bannerName: string) => {
      // Once dismissed, banner never shows again
      return dismissals.some(d => d.dismissedBanner === bannerName);
    },
    [dismissals],
  );

  return {
    shouldShowBannersToday,
    dismissBanner,
    isBannerDismissed,
    dismissals,
  };
}
